import React, { useEffect, useState } from "react";
import { usersResource } from "../../helpers/configRoutes/backend_helper";
import { Link, useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../components/modal/ConfirmDeleteModal";
import { userColumns } from "./Columns";
import DataTableElement from "../../components/elements/DataTableElement";
import usePlanUsage, { PLAN_FEATURES, usageBlockedMessage, usageLimitText } from "../../hooks/usePlanUsage";
import RolModal from "../rols/RolModal";

const Team = () => {
    const navigate = useNavigate();

    const [modalConfirm, setModalConfirm] = useState({
        show: false,
        title: "Delete user",
        loading: false,
        textLoad: "Processing...",
    });

    const [tableLoad, setTableLoad] = useState({
        show: false,
        textLoad: "Loading users...",
    });
    const [modalRol, setModalRol] = useState({
        show: false,
        rol: null,
        title: "Role permissions",
    });

    const [filters] = useState({ perPage: 20, page: 1 });
    const [users, setUsers] = useState([]);
    const { getFeature, canUse, refresh } = usePlanUsage();
    const usersFeature = getFeature(PLAN_FEATURES.USERS);
    const canAddUser = canUse(PLAN_FEATURES.USERS);

    useEffect(() => {
        const getUsers = async () => {
            setTableLoad((prev) => ({ ...prev, show: true }));
            const response = await usersResource("get", { params: filters });
            if (response.status) {
                setUsers(response.users);
            }
            setTableLoad((prev) => ({ ...prev, show: false }));
        };
        getUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteUser = async () => {
        setModalConfirm((prev) => ({ ...prev, loading: true }));
        const response = await usersResource("del", {}, modalConfirm.id);
        if (response.status) {
            const updated = users.filter((u) => u.id !== modalConfirm.id);
            setUsers(updated);
            await refresh();
        }
        setModalConfirm({
            show: false,
            title: "Delete user",
            loading: false,
            textLoad: "Processing...",
            id: null,
        });
    };

    const openRoleModal = (user) => {
        if (!user?.role_id) return;

        setModalRol({
            show: true,
            rol: {
                id: user.role_id,
                name: user.role || user.rol?.name || "Role",
            },
            title: user.role || user.rol?.name || "Role permissions",
        });
    };

    const cols = userColumns(navigate, modalConfirm, setModalConfirm, openRoleModal);

    return (
        <>
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-cs-dark">
                            Team & Users
                        </h1>
                        <p className="text-sm text-cs-muted mt-1">
                            Manage all users with access to your workspace. Click a role badge to inspect its permissions.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="px-3 py-1.5 rounded-xl bg-cs-bg border border-cs-border text-[11px] text-cs-muted">
                            Total members:{" "}
                            <span className="font-semibold text-cs-dark">
                                {users.length}
                            </span>
                        </div>
                        <div className="px-3 py-1.5 rounded-xl bg-cs-bg border border-cs-border text-[11px] text-cs-muted">
                            Plan users:{" "}
                            <span className="font-semibold text-cs-dark">
                                {usageLimitText(usersFeature, "users and operators")}
                            </span>
                        </div>
                        {canAddUser ? (
                            <Link
                                to="/team-upadd"
                                className="
                inline-flex items-center gap-2
                px-4 py-2.5
                rounded-xl
                bg-cs-primary hover:bg-cs-primary-hover
                text-white text-sm font-medium
                shadow-md hover:shadow-lg
                transition-all
              "
                            >
                                <span className="material-symbols-outlined text-sm">add</span>
                                Add member
                            </Link>
                        ) : (
                            <button
                                type="button"
                                disabled
                                title={usageBlockedMessage(usersFeature, "usuarios")}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-300 text-gray-600 text-sm font-medium cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-sm">lock</span>
                                Limit reached
                            </button>
                        )}
                    </div>
                </div>

                {usersFeature?.is_over_limit && (
                    <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                        <p className="text-xs uppercase tracking-[0.16em] text-red-700">
                            Over limit
                        </p>
                        <p className="mt-1 text-sm text-cs-dark font-medium">
                            This plan allows {usersFeature.limit} users and your company currently has {usersFeature.used}.
                        </p>
                        <p className="mt-1 text-xs text-cs-muted">
                            You must remove or deactivate members, or upgrade the subscription, before adding new users again.
                        </p>
                    </div>
                )}

                {/* Table wrapper */}
                <div
                    className="
            bg-cs-bg border border-cs-border rounded-2xl
            shadow-sm px-2 py-2
          "
                >
                    <DataTableElement
                        columns={cols}
                        items={users}
                        tableLoad={tableLoad}
                    />
                </div>
            

            <ConfirmDeleteModal
                modal={modalConfirm}
                setModal={setModalConfirm}
                action={deleteUser}
            />

            {modalRol.show && (
                <RolModal
                    modal={modalRol}
                    setModal={setModalRol}
                />
            )}
        </>
    );
};

export default Team;
